import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import type { IDirectoriesService, TDictObject } from '../../shared/IDirectoriesService';
import {
  areFiltersValuesEqual,
  copyFiltersValues,
  deserializeFiltersValues,
  EMPTY_FILTERS_VALUES,
  isFiltersValuesEmpty,
  serializeFiltersValues,
  type FiltersMultiValue,
  type FiltersValues,
  type FiltersWidgetStateParams,
} from '../types/state';

export type FiltersSelectOption = {
  value: string | number;
  label: string;
};

export const FILTERS_DIRECTORY_TYPES = [
  'Common_Organization',
  'Common_Block',
  'Common_Field',
  'Common_Pad',
  'GOdashboard_Well',
  'Common_Rig_Model',
  'Common_Brigade',
  'Common_WellboreTypeInstance',
] as const;

const STATUS_OPTIONS_RAW = [
  { labelKey: 'active', value: 'ACTIVE' },
  { labelKey: 'completed', value: 'COMPLETED' },
] as const;

function mapDirectoryToOptions(directory: TDictObject[] | null, refObjectAttr: string): FiltersSelectOption[] {
  if (!directory) return [];

  const options: FiltersSelectOption[] = [];

  for (const item of directory) {
    const labelValue = item.data[refObjectAttr];
    if (labelValue == null || item.id == null) continue;

    options.push({
      value: item.id,
      label: String(labelValue),
    });
  }

  return options;
}

export class FiltersService {
  @observable.ref draft: FiltersValues = copyFiltersValues(EMPTY_FILTERS_VALUES);

  @observable.ref applied: FiltersValues = copyFiltersValues(EMPTY_FILTERS_VALUES);

  @observable isLoading = false;

  private onAppliedChange?: (state: FiltersWidgetStateParams) => void;

  constructor(private readonly directoriesService: IDirectoriesService) {
    makeObservable(this);
  }

  @computed
  get isDirty(): boolean {
    return !areFiltersValuesEqual(this.draft, this.applied);
  }

  @computed
  get isApplyDisabled(): boolean {
    return !this.isDirty;
  }

  @computed
  get isCancelDisabled(): boolean {
    return !this.isDirty;
  }

  @computed
  get isResetDisabled(): boolean {
    return isFiltersValuesEmpty(this.draft) && isFiltersValuesEmpty(this.applied);
  }

  @action.bound
  hydrate(stateParams?: FiltersWidgetStateParams | null): void {
    const values = deserializeFiltersValues(stateParams?.values);
    this.draft = copyFiltersValues(values);
    this.applied = copyFiltersValues(values);
  }

  @action.bound
  setOnAppliedChange(callback?: (state: FiltersWidgetStateParams) => void): void {
    this.onAppliedChange = callback;
  }

  getSnapshot(): FiltersWidgetStateParams {
    return {
      values: serializeFiltersValues(this.applied),
    };
  }

  @action.bound
  setStatus(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, status: value.map(String) };
  }

  @action.bound
  setWorkStartDateFrom(value: number | null): void {
    this.draft = { ...this.draft, workStartDateFrom: value };
  }

  @action.bound
  setWorkStartDateTo(value: number | null): void {
    this.draft = { ...this.draft, workStartDateTo: value };
  }

  @action.bound
  setOrganizationId(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, organizationId: [...value] };
  }

  @action.bound
  setBlockId(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, blockId: [...value] };
  }

  @action.bound
  setFieldId(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, fieldId: [...value] };
  }

  @action.bound
  setPadId(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, padId: [...value] };
  }

  @action.bound
  setWellId(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, wellId: [...value] };
  }

  @action.bound
  setRigModelId(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, rigModelId: [...value] };
  }

  @action.bound
  setBrigadeId(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, brigadeId: [...value] };
  }

  @action.bound
  setWellboreTypeInstanceId(value: FiltersMultiValue): void {
    this.draft = { ...this.draft, wellboreTypeInstanceId: [...value] };
  }

  @action.bound
  apply(): void {
    this.applied = copyFiltersValues(this.draft);
    this.onAppliedChange?.(this.getSnapshot());
  }

  @action.bound
  cancel(): void {
    this.draft = copyFiltersValues(this.applied);
  }

  @action.bound
  reset(): void {
    this.draft = copyFiltersValues(EMPTY_FILTERS_VALUES);
    this.applied = copyFiltersValues(EMPTY_FILTERS_VALUES);
    this.onAppliedChange?.(this.getSnapshot());
  }

  getStatusOptions(t: (key: string) => string): FiltersSelectOption[] {
    return STATUS_OPTIONS_RAW.map((option) => ({
      value: option.value,
      label: t(`Labels:${option.labelKey}.label`),
    }));
  }

  getOrganizationOptions(): FiltersSelectOption[] {
    return mapDirectoryToOptions(this.directoriesService.getObject('Common_Organization'), 'name');
  }

  getBlockOptions(): FiltersSelectOption[] {
    return mapDirectoryToOptions(this.directoriesService.getObject('Common_Block'), 'name');
  }

  getFieldOptions(): FiltersSelectOption[] {
    return mapDirectoryToOptions(this.directoriesService.getObject('Common_Field'), 'name');
  }

  getPadOptions(): FiltersSelectOption[] {
    return mapDirectoryToOptions(this.directoriesService.getObject('Common_Pad'), 'abbreviatedName');
  }

  getWellOptions(): FiltersSelectOption[] {
    return mapDirectoryToOptions(this.directoriesService.getObject('GOdashboard_Well'), 'name');
  }

  getRigModelOptions(): FiltersSelectOption[] {
    return mapDirectoryToOptions(this.directoriesService.getObject('Common_Rig_Model'), 'name');
  }

  getBrigadeOptions(): FiltersSelectOption[] {
    return mapDirectoryToOptions(this.directoriesService.getObject('Common_Brigade'), 'name');
  }

  getWellboreTypeInstanceOptions(): FiltersSelectOption[] {
    return mapDirectoryToOptions(this.directoriesService.getObject('Common_WellboreTypeInstance'), 'name');
  }

  async loadDirectories(): Promise<void> {
    this.isLoading = true;

    try {
      await this.directoriesService.loadObjects([...FILTERS_DIRECTORY_TYPES]);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  dispose(): void {
    this.onAppliedChange = undefined;
  }
}
